import { alpha, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/styles';
import { memo, useMemo, useState, useEffect, useReducer, Reducer } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuthUser } from '../../contexts/AuthProvider/hooks';
import { useConfig } from '../../eliot-components/src/ConfigProvider/hooks';
import Layout from '../../Layout/Layout';
import Loading from '../../eliot-components/src/Loading/Loading';
import Filters, {
  lastStatusCodeFilterWrapper,
} from './components/Filters/Filters';
import {
  filtersFromQueryString,
  queryStringFromFilters,
} from './components/Filters/queryString';
import { ListDecoratedMedisanteDevice, DevicesSortBy } from './types';
import wrapFilters from './components/Filters/wrapFilters';
import { Paper, IconButton } from '@material-ui/core';
import Visible from '../../components/Visible/Visible';
import ActionsMenu from '../../components/ActionsMenu/ActionsMenu';
import { MediaUp, MediaDown } from '../../components/MediaQueries/MediaQueries';
import MobileLayout from './components/MobileLayout/MobileLayout';
import Back from '@material-ui/icons/ChevronLeft';
import { useSortState, SortState } from '../../utils/sort';
import {
  devicesSortingReducer,
  sortDevices,
  mobileSortOptions,
  decorateListWithStatuses,
} from './config';
import { useManageContext } from '../../contexts/ManageContext/ManageContext';
import DevicesTable from './components/DevicesTable/DevicesTable';
import { allGatewayDevices } from '../Device/constants';
import { DeviceModels } from '../Device/types';
import { isDarkTheme } from '../../eliot-components/src/theme';
import clsx from 'clsx';
import {
  useGetDevicesQuery,
  useGetTargetSystemsQuery,
} from '../../store/api-slice';
import { useAppSelector } from '../../hooks';
import { IDialogState } from '../../components/ActionsMenu/types';
import { dialogStateReducer } from '../../components/ActionsMenu/config';
import ActionMenuDialogs from '../../components/ActionsMenu/ActionMenuDialogs';
import { isTotalMeasurementCount, mergeFilters } from '../../utils/helpers';

const useStyles = makeStyles((theme: Theme) => {
  return {
    placeholder: {
      textAlign: 'center',
      marginTop: theme.spacing(5),
    },
    selection: {
      color: 'white',
      backgroundColor: theme.palette.primary.main,
      padding: theme.spacing(1, 2),
      display: 'flex',
      '& > p': {
        flexGrow: 1,
        alignSelf: 'center',
        fontWeight: 500,
        textTransform: 'uppercase',
      },
      [theme.breakpoints.down('sm')]: {
        position: 'fixed',
        top: '0px',
        left: '0px',
        right: '0px',
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        padding: '0px',
        boxShadow:
          '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
        minHeight: '64px',
        '& > p': {
          display: 'flex',
          alignItems: 'center',
        },
      },
      [theme.breakpoints.down('xs')]: {
        minHeight: '56px',
      },
    },
    selectionBackButton: {
      color: 'white',
    },
    actions: {
      '& > .MuiButton-textPrimary': {
        color: theme.palette.primary.contrastText,
      },
    },
    page: {
      display: 'flex',
      flexGrow: 1,
      flexDirection: 'column',
      [theme.breakpoints.up('md')]: {
        height: '100%',
      },
      '& .mainContent': {
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.down('sm')]: {
          paddingBottom: theme.spacing(12),
        },
        [theme.breakpoints.up('lg')]: {
          width: 'calc(100% - 300px)',
        },
      },
      '& .overflow-box': {
        overflow: 'visible',
        height: 0,
        flexGrow: 1,
      },
      '& .table-box': {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
      },
      '& .paper-box': {
        [theme.breakpoints.down('sm')]: {
          overflow: 'visible',
          flexGrow: 0,
          border: 'none',
          boxShadow: 'none',
          backgroundColor: 'transparent',
        },
        position: 'relative',
        overflow: 'hidden',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
      },
    },
    updatingState: {
      backgroundColor: theme.palette.secondary.light,
      display: 'inline-flex',
      alignItems: 'center',
      position: 'relative',
      top: 2,
      borderRadius: theme.shape.borderRadius,
      padding: '2px 8px 2px 4px',
      color: 'white',
      '& svg': {
        marginRight: 2,
      },
    },
    icon: {
      marginLeft: theme.spacing(1),
    },
    summary: {
      height: '48px',
      backgroundColor: isDarkTheme(theme)
        ? alpha(theme.palette.secondary.dark, 0.3)
        : '#eeeeee',
      borderTop: '2px solid rgba(255, 255, 255, 0.12)',
    },
    summaryItem: {
      padding: '0 15px',
    },
    cellHeader: {
      backgroundColor: isDarkTheme(theme)
        ? alpha(theme.palette.secondary.dark, 0.3)
        : '#eeeeee',
    },

    table: {
      '& div[data-parity=even]': {
        backgroundColor: isDarkTheme(theme)
          ? alpha(theme.palette.secondary.dark, 0.3)
          : alpha('#eeeeee', 0.3),
      },
      '& div[data-parity][data-hovered=true]': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  };
});

const Devices = memo(() => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const config = useConfig();
  const { state: globalState, dispatch } = useManageContext();
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const { authUser } = useAuthUser();
  const [actionMenuDialogState, actionMenuUpdateDialogState] = useReducer<
    Reducer<IDialogState, string>
  >(dialogStateReducer, { isMenuOpen: false });
  const reduxFilters = useAppSelector((state) => state.appState.deviceFilters);
  // read filters from redux or from url (only on refresh or when is not in redux yet)
  // filters are set to redux inside Filters component
  const filters = useMemo(() => {
    let queryFilters = filtersFromQueryString(location.search);
    return wrapFilters(mergeFilters(reduxFilters, queryFilters));
  }, [location.search, reduxFilters]);
  useEffect(() => {
    try {
      const search = queryStringFromFilters(filters);
      if (history.location.search !== search) {
        history.replace({ search });
      }
    } catch (e) {
      console.warn('error parsing filters: ' + e);
    }
  }, [history, filters]);

  const { data: targetSystems = [] } = useGetTargetSystemsQuery({
    apiBaseURL: config.apiBaseURL,
    orgs: authUser.selectedOrgs,
    filters: [],
  });

  const deviceFilters = useMemo(
    () => lastStatusCodeFilterWrapper(filters, targetSystems),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, targetSystems]
  );

  const globalSearch = deviceFilters.find(
    (d) => d.category === 'serial_number_or_imei'
  );

  const {
    isFetching: isLoading,
    error,
    data: fetchedDevicesData,
  } = useGetDevicesQuery({
    apiBaseURL: config.apiBaseURL,
    filters: deviceFilters,
    orgs: !!globalSearch ? [] : authUser.selectedOrgs,
  });
  const fetchedDevices = fetchedDevicesData ?? [];
  const totalView = isTotalMeasurementCount();

  let decoratedList = fetchedDevices;

  let selectableDevices = useMemo(
    () => decorateListWithStatuses(decoratedList, authUser),
    [decoratedList, authUser]
  );

  if (
    deviceFilters.some(
      (e) =>
        [
          'measurementGreaterThan',
          'measurementLessThan',
          'measurementEquals',
        ].indexOf(e.category) > -1
    )
  ) {
    selectableDevices = selectableDevices.filter((dev) => {
      return (
        dev.model !== DeviceModels.GW9017 && dev.model !== DeviceModels.ISGI
      );
    });
  }

  if (deviceFilters.some((e) => e.category === 'onlyArchived')) {
    selectableDevices = selectableDevices.filter((dev) => {
      return dev.isArchived;
    });
  }

  if (deviceFilters.some((e) => e.category === 'onlyStock')) {
    selectableDevices = selectableDevices.filter((dev) => {
      return dev.inStock;
    });
  }

  if (deviceFilters.some((e) => e.category === 'onlyBlocked')) {
    selectableDevices = selectableDevices.filter((dev) => {
      return dev.deactivated;
    });
  }

  //@ts-ignore
  const { sortState, updateSortState, currentOptionId } = useSortState<
    SortState<ListDecoratedMedisanteDevice>,
    DevicesSortBy
  >(
    devicesSortingReducer,
    globalState.sortStates.devicesSortState,
    () => {
      dispatch({
        type: 'change-devices-sort-state',
        data: sortState,
      });
    },
    mobileSortOptions
  );

  const { userOrgs } = globalState;
  const devices = useMemo(
    () => sortDevices(selectableDevices, sortState, userOrgs || [], totalView),
    [selectableDevices, sortState, userOrgs, totalView]
  );

  const allAreSelected = devices
    ? devices.every((d) => selectedDeviceIds.indexOf(d.id) !== -1)
    : false;
  const someAreSelected =
    !allAreSelected &&
    !!devices &&
    devices.some((d) => selectedDeviceIds.indexOf(d.id) !== -1);

  const handleToggleDeviceSelectedStatus = (id: string) => {
    if (selectedDeviceIds.indexOf(id) === -1) {
      setSelectedDeviceIds(selectedDeviceIds.concat([id]));
      return;
    }

    setSelectedDeviceIds(
      selectedDeviceIds.filter((d) => {
        return d !== id;
      })
    );
  };

  const handleToggleAllDevicesSelectedStatus = () => {
    if (allAreSelected) {
      setSelectedDeviceIds([]);
      return;
    }
    setSelectedDeviceIds(devices.map((d) => d.id));
  };

  const selectedCount = selectedDeviceIds.length;

  const selectedDevices = devices
    ? devices.filter((item: ListDecoratedMedisanteDevice) => {
        return selectedDeviceIds.indexOf(item.id) !== -1;
      })
    : [];

  const measurementCount = devices
    ? devices
        .filter((d) => !allGatewayDevices.includes(d.model as DeviceModels))
        .reduce(
          (agg, device) =>
            agg +
            (totalView
              ? device.measurementCount || 0
              : device.measurementTripCount || 0),
          0
        )
    : 0;
  const shouldShowDevicesTable = devices && devices.length > 0;

  const noTableMessage =
    fetchedDevices.length === 0 ? 'No devices found...' : null;

  return (
    <div className={clsx(classes.page, { withFilters: filters.length > 0 })}>
      <Layout error={error}>
        <Filters
          filters={filters}
          visibleDevices={fetchedDevices ? fetchedDevices : []}
          targetSystems={targetSystems}
          deviceCount={devices ? devices.length : 0}
          measurementCount={measurementCount}
          sortOptionId={currentOptionId}
          updateSortState={updateSortState}
        />
        {isLoading || devices === null ? (
          <Loading />
        ) : (
          <>
            {shouldShowDevicesTable ? (
              <Paper className="paper-box">
                <Visible when={selectedCount > 0 && selectedDevices.length > 0}>
                  <div className={classes.selection}>
                    <Typography variant="body2">
                      <MediaDown query="sm">
                        <IconButton
                          className={classes.selectionBackButton}
                          onClick={() => setSelectedDeviceIds([])}
                        >
                          <Back />
                        </IconButton>
                      </MediaDown>
                      <span>
                        {selectedCount}
                        <Visible when={selectedCount === 1}> device </Visible>
                        <Visible when={selectedCount > 1}> devices </Visible>
                        <MediaUp query="md">selected</MediaUp>
                      </span>
                    </Typography>
                    <div className={classes.actions}>
                      <ActionsMenu
                        devices={selectedDevices}
                        dialogState={actionMenuDialogState}
                        updateDialogState={actionMenuUpdateDialogState}
                      />
                    </div>
                  </div>
                </Visible>
                <MediaDown query="sm">
                  <MobileLayout
                    totalView={totalView}
                    devices={devices}
                    selectedDeviceIds={selectedDeviceIds}
                    onSelectionChanged={handleToggleDeviceSelectedStatus}
                    onSelectAllChanged={handleToggleAllDevicesSelectedStatus}
                    selectAll={{ all: allAreSelected, some: someAreSelected }}
                    sortOptionId={currentOptionId}
                    updateSortState={updateSortState}
                  />
                </MediaDown>
                <MediaUp query="md">
                  <DevicesTable
                    totalView={totalView}
                    devices={devices}
                    selectedDeviceIds={selectedDeviceIds}
                    onSelectionChanged={handleToggleDeviceSelectedStatus}
                    onSelectAllChanged={handleToggleAllDevicesSelectedStatus}
                    selectAll={{ all: allAreSelected, some: someAreSelected }}
                    sortState={sortState}
                    actionMenuDialogState={actionMenuDialogState}
                    actionMenuUpdateDialogState={actionMenuUpdateDialogState}
                    updateSortState={updateSortState}
                    measurementCount={measurementCount}
                  />
                </MediaUp>
              </Paper>
            ) : (
              <Typography
                color="textSecondary"
                className={classes.placeholder}
                paragraph
              >
                {noTableMessage}
              </Typography>
            )}
          </>
        )}
        <ActionMenuDialogs
          dialogState={actionMenuDialogState}
          updateDialogState={actionMenuUpdateDialogState}
          actualDevices={fetchedDevices || []}
        />
      </Layout>
    </div>
  );
});

export default Devices;
